import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import FormGuide from "@/components/FormGuide";
import HoverPanel from "@/components/HoverPanel";
import PredictionPanel from "@/components/PredictionPanel";
import GoalDistributionPanel from "@/components/GoalDistributionPanel";
import H2HPanel from "@/components/H2HPanel";
import StandingsTable from "@/components/StandingsTable";
import AlgorithmPanel from "@/components/AlgorithmPanel";
import OddsPanel from "@/components/OddsPanel";
import AdvancedMetricsRow from "@/components/AdvancedMetricsRow";
import ScoreHeatmap from "@/components/ScoreHeatmap";
import MonteCarloPanel from "@/components/MonteCarloPanel";
import MatchFlagsPanel from "@/components/MatchFlagsPanel";
import MatchNavArrows from "@/components/MatchNavArrows";
import { AIAnalysisPanel } from "@/components/AIAnalysisPanel";
import StatsComparisonPanel from "@/components/StatsComparisonPanel";
import { MatchPageSkeleton } from "@/components/MatchPageSkeleton";
import { RefereePanel } from "@/components/RefereePanel";
import { InjuriesPanel } from "@/components/InjuriesPanel";
import PlayProfilePanel, { loadActiveProfile, saveActiveProfile, loadProfileEnabled, saveProfileEnabled } from "@/components/PlayProfilePanel";
import MatchDNAPanel from "@/components/MatchDNAPanel";
import MonolitePanel from "@/components/MonolitePanel";
import { ArrowLeft, AlertCircle, CheckCircle2, ShoppingCart, Zap, Crosshair } from "lucide-react";
import SenseiPanel from "@/components/SenseiPanel";
import { checkSenseiEligibility } from "@/lib/sensei";
import PremiumGate from "@/components/PremiumGate";
import { usePremium } from "@/contexts/AuthContext";
import useMatchPreview from "@/hooks/useMatchPreview";
import { Sparkles as SparklesIcon, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AI_ENABLED = process.env.REACT_APP_ENABLE_AI === "true";
const C = {
  bg: "var(--bc-bg, #020202)", surface: "var(--bc-panel, #09090B)", highlight: "var(--bc-panel-elev, #141416)",
  border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)",
  green: "#22C55E", cyan: "#06B6D4", blue: "#3B82F6", yellow: "#F59E0B", red: "#EF4444",
};
const SELECTIONS_KEY = "bc_my_bets";

const getSessionId = () => {
  let sid = localStorage.getItem("betcracker_session_id");
  if (!sid) { sid = crypto.randomUUID?.() || Math.random().toString(36).slice(2); localStorage.setItem("betcracker_session_id", sid); }
  return sid;
};

const STATUS_MAP = {
  "NS": { label: "NON INIZIATA", color: C.muted, bg: C.highlight },
  "1H": { label: "1° TEMPO", color: C.green, bg: "rgba(34,197,94,0.1)" },
  "HT": { label: "INTERVALLO", color: C.yellow, bg: "rgba(245,158,11,0.1)" },
  "2H": { label: "2° TEMPO", color: C.green, bg: "rgba(34,197,94,0.1)" },
  "FT": { label: "FINALE", color: C.muted, bg: C.highlight },
  "ET": { label: "SUPPL.", color: C.blue, bg: "rgba(59,130,246,0.1)" },
  "P": { label: "RIGORI", color: C.blue, bg: "rgba(59,130,246,0.1)" },
};

function GundamScorePanel({ gundamScores, actualHome, actualAway, isFinished, isEuropeanCup, homeTeam, awayTeam, onAddBet, selectedBets }) {
  if (!gundamScores || gundamScores.length === 0) return null;
  const isSelected = (bet) => (selectedBets || []).some((b) => b.bet === bet);

  return (
    <div data-testid="gundam-score-panel"
      style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `2px solid #A78BFA` }}>
      <div className="px-4 pt-4 pb-3">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Crosshair size={14} style={{ color: "#A78BFA" }} />
          <span className="font-heading font-black text-xs uppercase tracking-[0.2em]" style={{ color: "#A78BFA" }}>
            GUNDAM SCORE
          </span>
          {homeTeam && awayTeam && (
            <span className="font-heading font-bold text-xs uppercase tracking-wide"
              style={{ color: C.muted, flexShrink: 0 }}>
              &nbsp;·&nbsp;{homeTeam}&nbsp;<span style={{ color: "#A78BFA" }}>vs</span>&nbsp;{awayTeam}
            </span>
          )}
          <span className="font-data text-[10px] uppercase tracking-widest ml-auto" style={{ color: C.muted }}>
            Risultati Esatti Più Probabili
          </span>
          {isEuropeanCup && (
            <span className="font-data text-[10px] px-2 py-0.5 uppercase tracking-wider"
              style={{ color: "#A78BFA", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.2)" }}>
              COPPA INT.
            </span>
          )}
        </div>

        {/* Score Cards */}
        <div className="grid grid-cols-3 gap-3">
          {gundamScores.map((s, i) => {
            const isCorrect = isFinished && actualHome === s.home && actualAway === s.away;
            const isWrong = isFinished && !isCorrect;
            const betLabel = `Risultato esatto ${s.score}`;
            const picked = isSelected(betLabel);
            const canPick = !!onAddBet && !isFinished;
            const handleClick = canPick
              ? () => onAddBet({ bet: betLabel, prob: s.prob, type: "scoreexact" })
              : undefined;
            return (
              <button
                key={s.score}
                type="button"
                data-testid={`gundam-score-${i}`}
                onClick={handleClick}
                disabled={!canPick}
                aria-pressed={picked}
                title={canPick ? (picked ? "Rimuovi dai miei pronostici" : "Aggiungi ai miei pronostici") : undefined}
                className="flex flex-col items-center justify-center py-4 relative transition-all"
                style={{
                  background: isCorrect
                    ? "rgba(34,197,94,0.08)"
                    : picked
                    ? "rgba(167,139,250,0.18)"
                    : "var(--bc-panel-elev, rgba(255,255,255,0.025))",
                  border: `1px solid ${
                    isCorrect ? "rgba(34,197,94,0.4)"
                    : picked ? "#A78BFA"
                    : "rgba(167,139,250,0.15)"
                  }`,
                  boxShadow: picked ? "0 0 10px rgba(167,139,250,0.4)" : "none",
                  opacity: isFinished && isWrong ? 0.4 : 1,
                  cursor: canPick ? "pointer" : "default",
                  outline: "none",
                }}>
                {/* Rank badge */}
                <span className="absolute top-1.5 left-2 font-data text-[9px]"
                  style={{ color: i === 0 ? "#A78BFA" : C.muted }}>
                  #{i + 1}
                </span>
                {/* Score */}
                <span className="font-heading font-black text-2xl tracking-tight tabular-nums"
                  style={{ color: isCorrect ? C.green : picked ? "#A78BFA" : C.text }}>
                  {s.score}
                </span>
                {/* Probability */}
                <span className="font-data text-xs mt-1 tabular-nums"
                  style={{ color: i === 0 || picked ? "#A78BFA" : C.muted }}>
                  {s.prob}%
                </span>
                {/* Correct indicator */}
                {isCorrect && (
                  <div className="absolute top-1.5 right-2">
                    <CheckCircle2 size={12} style={{ color: C.green }} />
                  </div>
                )}
                {/* Selected indicator */}
                {picked && !isCorrect && (
                  <span className="absolute top-1.5 right-2 font-heading font-black"
                    style={{ color: "#A78BFA", fontSize: 11, lineHeight: 1 }}>
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {isEuropeanCup && (
          <p className="font-data text-[10px] mt-3" style={{ color: C.muted }}>
            Le previsioni per coppe europee usano un fattore conservativo: la distribuzione dei gol è ricalibrata verso la media storica UCL/UEL/UECL (~1.15 gol/sq).
          </p>
        )}
      </div>
    </div>
  );
}

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ─── Profilo di Gioco: mappa ordine pannelli + render ──────────────
// Ogni voce è un id che corrisponde a una key in ``PANEL_RENDERERS``.
// Cambiando profilo, l'utente riordina la sezione "Pronostici" in modo
// che i pannelli più rilevanti per il suo stile compaiano in cima.
const PANEL_ORDER = {
  conservatore: [
    "prediction", "match-flags", "goal-dist", "odds",
    "advanced-metrics", "heatmap", "gundam", "monte-carlo",
  ],
  value: [
    "odds", "advanced-metrics", "match-flags", "prediction",
    "monte-carlo", "heatmap", "gundam", "goal-dist",
  ],
  combo: [
    "goal-dist", "advanced-metrics", "prediction", "gundam",
    "odds", "monte-carlo", "heatmap", "match-flags",
  ],
  exact: [
    "gundam", "heatmap", "monte-carlo", "advanced-metrics",
    "prediction", "odds", "goal-dist", "match-flags",
  ],
};
// Ordine classico: usato quando l'utente disattiva la modalità Profili.
// Replica esattamente la disposizione storica della MatchPage.
const CLASSIC_ORDER = [
  "match-flags", "prediction", "goal-dist",
  "advanced-metrics", "odds", "gundam", "heatmap", "monte-carlo",
];

function renderPronosticiPanels({ profile, profileEnabled, data, predictions, home_team, away_team, handleAddBet, selectedBets, isPremium, forceUnlock = false }) {
  const order = profileEnabled
    ? (PANEL_ORDER[profile] || PANEL_ORDER.conservatore)
    : CLASSIC_ORDER;
  // Premium-only sections: blurred with CTA for Free users.
  const PREMIUM_PANELS = {
    "goal-dist":         { label: "Distribuzione Gol Avanzata", reason: "Tilt multifactor + Negative Binomial" },
    "advanced-metrics":  { label: "Analisi Avanzata",           reason: "Elm · Inclinazione · Tendenza" },
    "odds":              { label: "Quote + Value Bet",          reason: "Sharp Pinnacle + Shin de-vig" },
    "gundam":            { label: "Gundam Score",               reason: "Top 3 risultati esatti" },
    "heatmap":           { label: "Heatmap Risultati",          reason: "Matrice 5×5 Dixon-Coles" },
    "monte-carlo":       { label: "Monte Carlo",                reason: "10.000 simulazioni" },
  };
  const renderers = {
    "match-flags": () => (
      <MatchFlagsPanel
        flags={data.flags}
        homeName={home_team.name}
        awayName={away_team.name}
      />
    ),
    "prediction": () => (
      <HoverPanel color="rgba(34,197,94,0.20)" borderGlow="rgba(34,197,94,0.35)">
        <PredictionPanel
          predictions={predictions}
          homeTeam={home_team.name}
          awayTeam={away_team.name}
          onAddBet={handleAddBet}
          selectedBets={selectedBets}
          isPremium={isPremium || forceUnlock}
        />
      </HoverPanel>
    ),
    "goal-dist": () => (
      <HoverPanel color="rgba(167,139,250,0.20)" borderGlow="rgba(167,139,250,0.35)">
        <GoalDistributionPanel
          multigol={predictions.multigol}
          goalsByHalf={predictions.goals_by_half}
          homeTeam={home_team.name}
          awayTeam={away_team.name}
          onAddBet={handleAddBet}
          selectedBets={selectedBets}
        />
      </HoverPanel>
    ),
    "advanced-metrics": () => (
      <AdvancedMetricsRow
        metrics={predictions.advanced_metrics}
        homeTeam={home_team.name}
        awayTeam={away_team.name}
      />
    ),
    "odds": () => (
      predictions.odds?.odds ? (
        <OddsPanel oddsData={predictions.odds.odds} valueData={predictions.value} combined={predictions.combined} />
      ) : null
    ),
    "gundam": () => (
      <GundamScorePanel
        gundamScores={predictions.gundam_scores}
        actualHome={data?.home_team?.goals}
        actualAway={data?.away_team?.goals}
        isFinished={["FT", "AET", "PEN"].includes(data?.fixture?.status)}
        isEuropeanCup={predictions.is_european_cup}
        homeTeam={home_team.name}
        awayTeam={away_team.name}
        onAddBet={handleAddBet}
        selectedBets={selectedBets}
      />
    ),
    "heatmap": () => (
      <HoverPanel color="rgba(167,139,250,0.20)" borderGlow="rgba(167,139,250,0.35)">
        <ScoreHeatmap
          heatmap={predictions.heatmap_5x5}
          homeTeam={home_team.name}
          awayTeam={away_team.name}
        />
      </HoverPanel>
    ),
    "monte-carlo": () => (
      <HoverPanel color="rgba(34,211,238,0.20)" borderGlow="rgba(34,211,238,0.35)">
        <MonteCarloPanel
          monteCarlo={predictions.monte_carlo}
          homeTeam={home_team.name}
          awayTeam={away_team.name}
        />
      </HoverPanel>
    ),
  };
  return order.map((id) => {
    const node = renderers[id]?.();
    if (!node) return null;
    const premiumInfo = PREMIUM_PANELS[id];
    if (premiumInfo && !isPremium) {
      return (
        <div key={id} data-panel-id={id} data-testid={`panel-${id}`}>
          <PremiumGate
            feature={premiumInfo.label}
            reason={premiumInfo.reason}
            testid={`premium-gate-${id}`}
            forceUnlock={forceUnlock}
          >
            {node}
          </PremiumGate>
        </div>
      );
    }
    return (
      <div key={id} data-panel-id={id} data-testid={`panel-${id}`}>
        {node}
      </div>
    );
  });
}

export default function MatchPage() {
  const { fixtureId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("pronostici");
  const [selectedBets, setSelectedBets] = useState([]);
  const [showAddedMsg, setShowAddedMsg] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiRequested, setAiRequested] = useState(false);
  const [senseiEligible, setSenseiEligible] = useState(false);
  const [senseiOpen, setSenseiOpen] = useState(false);
  const [playProfile, setPlayProfile] = useState(() => loadActiveProfile());
  const [profileEnabled, setProfileEnabled] = useState(() => loadProfileEnabled());
  const { isPremium } = usePremium();
  // Sneak peek: i Free possono sbloccare manualmente 1 sola partita a vita.
  const preview = useMatchPreview(fixtureId);
  // forceUnlock applica l'override: TRUE quando Premium OR il quota preview
  // permette di visualizzare questa partita.
  const forceUnlock = isPremium || preview.canView;

  const onProfileChange = (id) => {
    setPlayProfile(id);
    saveActiveProfile(id);
  };
  const onProfileToggle = (enabled) => {
    setProfileEnabled(enabled);
    saveProfileEnabled(enabled);
  };

  useEffect(() => {
    let cancelled = false;
    checkSenseiEligibility().then((eligible) => {
      if (!cancelled) setSenseiEligible(eligible);
    });
    return () => { cancelled = true; };
  }, []);

  // Load existing selections for this fixture from localStorage. Always reset
  // first — otherwise switching match keeps the previous match's picks in
  // memory if the new fixture has none yet (carrello "appiccicato").
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(SELECTIONS_KEY) || "[]");
      const existing = stored.find((s) => String(s.fixture_id) === String(fixtureId));
      setSelectedBets(existing?.bets || []);
    } catch (e) {
      console.error("[MatchPage] Failed to load saved bets:", e);
      setSelectedBets([]);
    }
  }, [fixtureId]);

  const handleAddBet = (bet) => {
    setSelectedBets((prev) => {
      const exists = prev.some((b) => b.bet === bet.bet);
      return exists ? prev.filter((b) => b.bet !== bet.bet) : [...prev, bet];
    });
  };

  const saveToMyBets = async (betsOverride) => {
    const bets = betsOverride || selectedBets;
    if (!data || bets.length === 0) return;
    const { fixture, home_team, away_team, league } = data;
    const sessionId = getSessionId();
    const payload = {
      fixture_id: parseInt(fixtureId),
      match: `${home_team.name} vs ${away_team.name}`,
      home: home_team.name, away: away_team.name,
      home_logo: home_team.logo, away_logo: away_team.logo,
      league: league.name, league_flag: league.flag, country: league.country,
      kick_off: fixture.date,
      bets,
      added_at: new Date().toISOString(),
    };
    try {
      // Save to localStorage (cache)
      const stored = JSON.parse(localStorage.getItem(SELECTIONS_KEY) || "[]");
      const filtered = stored.filter((s) => String(s.fixture_id) !== String(fixtureId));
      filtered.push(payload);
      localStorage.setItem(SELECTIONS_KEY, JSON.stringify(filtered));
      // Save to backend (persistent, cross-device)
      await axios.post(`${API}/user-bets`, {
        session_id: sessionId,
        fixture_id: parseInt(fixtureId),
        home_team: home_team.name, away_team: away_team.name,
        league_name: league.name, kick_off: fixture.date,
        bets,
      });
      setShowAddedMsg(true);
      setTimeout(() => setShowAddedMsg(false), 2000);
    } catch { setShowAddedMsg(true); setTimeout(() => setShowAddedMsg(false), 2000); }
  };

  // MONOLITE: aggiunge direttamente il pronostico unico ai "Miei Pronostici".
  const addMonolitePick = (pick) => {
    const merged = selectedBets.some((b) => b.bet === pick.bet)
      ? selectedBets
      : [...selectedBets, pick];
    setSelectedBets(merged);
    saveToMyBets(merged);
  };

  const fetchAI = async () => {
    if (aiLoading || aiAnalysis) return;
    setAiRequested(true);
    setAiLoading(true);
    try {
      const r = await axios.get(`${API}/match/${fixtureId}/ai`);
      setAiAnalysis(r.data?.ai_analysis || null);
    } catch {
      setAiAnalysis(null);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setAiAnalysis(null);
      try {
        const res = await axios.get(`${API}/match/${fixtureId}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Errore nel caricamento dell'analisi.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fixtureId]);

  if (loading) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh" }}>
        <Navbar />
        {/* Keep navigation arrows + keyboard shortcuts available while
            the match data is loading so the user can keep scrolling
            through fixtures without waiting for each FRESH compute. */}
        <MatchNavArrows
          fixtureId={fixtureId}
          onNavigate={(id) => navigate(`/match/${id}`)}
        />
        <MatchPageSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh" }}>
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32 gap-4 px-4">
          <AlertCircle size={36} style={{ color: C.red }} />
          <p className="font-heading text-2xl" style={{ color: C.red }}>ERRORE</p>
          <p className="text-sm text-center" style={{ color: C.muted }}>{error}</p>
          <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 text-xs font-heading uppercase tracking-widest" style={{ background: "var(--bc-accent)", color: "#09090B", fontWeight: 700 }}>
            TORNA INDIETRO
          </button>
        </div>
      </div>
    );
  }

  const { fixture, league, home_team, away_team, home_form, away_form, home_stats, away_stats, h2h, standings, predictions, real_stats, predicted_stats, season_avg_stats, injuries, sensei_overrides_applied } = data;
  const status = STATUS_MAP[fixture.status] || { label: fixture.status, color: C.muted, bg: C.highlight };
  const isLive = ["1H", "HT", "2H", "ET", "P"].includes(fixture.status);
  const isFinished = fixture.status === "FT";
  const hasScore = home_team.goals !== null && away_team.goals !== null;
  const senseiApplied = Array.isArray(sensei_overrides_applied) ? sensei_overrides_applied : [];

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>
      <Navbar />

      {/* Sensei overrides applied banner (mostra trasparenza calibrazioni AI) */}
      {senseiApplied.length > 0 && (
        <div data-testid="sensei-applied-banner" style={{
          maxWidth: 1280, margin: "0 auto", padding: "0.55rem 1rem",
          background: "rgba(255,111,216,0.07)",
          borderBottom: "1px solid rgba(255,111,216,0.25)",
          display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap",
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
        }}>
          <span style={{ color: "#FF6FD8", fontWeight: 700, letterSpacing: "0.08em" }}>
            ⚔ SENSEI · {senseiApplied.length} calibraz. AI attiv{senseiApplied.length === 1 ? "a" : "e"}
          </span>
          {senseiApplied.slice(0, 4).map((o, idx) => (
            <span key={idx} title={o.title || ""} style={{
              padding: "0.15rem 0.5rem", background: "rgba(180,135,255,0.1)",
              border: "1px solid rgba(180,135,255,0.3)", color: "#E0D0FF",
              fontSize: "0.65rem",
            }}>
              {o.market}: {o.before !== null && o.before !== undefined ? `${o.before}%→${o.after}%` : `Δ${o.delta_pp > 0 ? "+" : ""}${o.delta_pp}pp`}
            </span>
          ))}
          {senseiApplied.length > 4 && (
            <span style={{ color: "#71717A", fontSize: "0.65rem" }}>
              +{senseiApplied.length - 4} altre…
            </span>
          )}
        </div>
      )}

      {/* Prev / Next match navigation arrows */}
      <MatchNavArrows
        fixtureId={fixtureId}
        onNavigate={(id) => navigate(`/match/${id}`)}
      />

      {/* Back button */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 transition-colors hover:text-white"
            style={{ color: C.muted }}
          >
            <ArrowLeft size={15} />
            <span className="text-xs font-heading uppercase tracking-wider">Partite</span>
          </button>
          <span style={{ color: C.highlight }}>›</span>
          <div className="flex items-center gap-1.5">
            {league.flag && <img src={league.flag} alt="" className="w-4 h-3 object-cover" onError={(e) => e.target.style.display = "none"} />}
            {league.logo && <img src={league.logo} alt="" className="w-4 h-4 object-contain" onError={(e) => e.target.style.display = "none"} />}
            <span className="text-xs font-heading uppercase tracking-wider truncate" style={{ color: C.muted }}>
              {league.name} {league.round && `· ${league.round}`}
            </span>
          </div>
        </div>
      </div>

      {/* Match Header */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Status Badge */}
          <div className="flex justify-center mb-5">
            <span
              className="font-data text-xs px-3 py-1 uppercase tracking-widest"
              style={{ background: status.bg, color: status.color, border: `1px solid ${status.color}30` }}
            >
              {isLive && <span className="inline-block w-1.5 h-1.5 rounded-full mr-2 animate-live" style={{ background: C.green }} />}
              {status.label}
              {isLive && fixture.elapsed && ` ${fixture.elapsed}'`}
            </span>
          </div>

          {/* Teams */}
          <div className="flex items-center justify-between gap-4 bc-match-header-teams">
            {/* Home Team */}
            <div className="flex-1 flex flex-col items-center gap-3 bc-team-left" data-testid="home-team">
              <div className="w-16 h-16 flex items-center justify-center">
                <img src={home_team.logo} alt={home_team.name} className="max-w-full max-h-full object-contain"
                  onError={(e) => { e.target.style.display = "none"; }} />
              </div>
              <span className="font-heading font-bold text-center text-lg uppercase leading-tight tracking-tight" style={{ color: C.text }}>
                {home_team.name}
              </span>
            </div>

            {/* Score / Time */}
            <div className="flex flex-col items-center gap-2 min-w-[100px] bc-team-center">
              {hasScore ? (
                <div className="font-heading font-bold text-5xl tracking-tight" style={{ color: C.text }} data-testid="match-score">
                  {home_team.goals} <span style={{ color: C.highlight }}>–</span> {away_team.goals}
                </div>
              ) : (
                <div className="font-data text-2xl font-medium" style={{ color: C.text }} data-testid="match-time">
                  {new Date(fixture.date).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
              <span className="font-data text-xs" style={{ color: C.muted }}>
                {formatDateTime(fixture.date).split(",")[0]}
              </span>
            </div>

            {/* Away Team */}
            <div className="flex-1 flex flex-col items-center gap-3 bc-team-right" data-testid="away-team">
              <div className="w-16 h-16 flex items-center justify-center">
                <img src={away_team.logo} alt={away_team.name} className="max-w-full max-h-full object-contain"
                  onError={(e) => { e.target.style.display = "none"; }} />
              </div>
              <span className="font-heading font-bold text-center text-lg uppercase leading-tight tracking-tight" style={{ color: C.text }}>
                {away_team.name}
              </span>
            </div>
          </div>

          {/* Venue / Referee — inline compact info */}
          {(fixture.venue?.name || fixture.referee) && (
            <div className="flex justify-center gap-4 mt-4">
              {fixture.venue?.name && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs" style={{ color: "rgba(107,114,128,0.7)" }}>📍</span>
                  <span className="font-data text-xs" style={{ color: C.muted }}>{fixture.venue.name}</span>
                </div>
              )}
              {fixture.referee && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs" style={{ color: "rgba(107,114,128,0.7)" }}>👤</span>
                  <span className="font-data text-xs" style={{ color: C.muted }}>{fixture.referee}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 flex gap-0">
          {["pronostici", "statistiche"].map((tab) => (
            <button
              key={tab}
              data-testid={`tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className="px-6 py-3 text-xs font-heading uppercase tracking-widest transition-all duration-150"
              style={{
                color: activeTab === tab ? "var(--bc-accent)" : C.muted,
                borderBottom: activeTab === tab ? `2px solid var(--bc-accent)` : "2px solid transparent",
                fontWeight: 700,
              }}
            >
              {tab === "pronostici" ? "Pronostici" : "Statistiche"}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 animate-fade-in bc-cinematic" key={activeTab}>
        {activeTab === "pronostici" && (
          <div className="space-y-4">
            {/* MONOLITE — admin-only: un solo pronostico, il più affidabile */}
            <MonolitePanel fixtureId={fixtureId} onAddPick={addMonolitePick} />

            {/* Sensei AI — owner-only deep analysis trigger */}
            {senseiEligible && (
              senseiOpen ? (
                <SenseiPanel
                  fixtureId={data.fixture?.id || data.id}
                  matchPayload={{
                    home_team: home_team.name,
                    away_team: away_team.name,
                    league_name: data.league?.name || "",
                    match_date: data.fixture?.date || "",
                    combined: predictions?.combined || {},
                    target_kitano: predictions?.target_kitano || data.target_kitano || {},
                    home_stats: data.home_stats || {},
                    away_stats: data.away_stats || {},
                    home_form: data.home_form || [],
                    away_form: data.away_form || [],
                    h2h: data.h2h || [],
                    injuries: data.injuries || [],
                    advanced_metrics: predictions?.advanced_metrics || {},
                  }}
                  onClose={() => setSenseiOpen(false)}
                />
              ) : (
                <button
                  data-testid="sensei-open-btn"
                  onClick={() => setSenseiOpen(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    padding: "0.75rem 1rem",
                    width: "100%",
                    background: "color-mix(in srgb, var(--bc-accent) 5%, var(--bc-panel, #09090B))",
                    border: "1px dashed var(--bc-accent-medium)",
                    color: "var(--bc-accent)",
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: "0.85rem",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "color-mix(in srgb, var(--bc-accent) 10%, var(--bc-panel, #09090B))"; e.currentTarget.style.borderStyle = "solid"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "color-mix(in srgb, var(--bc-accent) 5%, var(--bc-panel, #09090B))"; e.currentTarget.style.borderStyle = "dashed"; }}
                >
                  <Zap size={14} />
                  ⚔ ATTIVA SENSEI AI
                  <span style={{ marginLeft: "auto", fontSize: "0.65rem", fontFamily: "'JetBrains Mono',monospace", letterSpacing: "0.14em", color: "rgba(255,255,255,0.4)" }}>
                    OWNER · Devil's Advocate · Value Scan · Kelly
                  </span>
                </button>
              )
            )}

            {/* Sneak-peek banner — only for Free users actively using a preview slot */}
            {!isPremium && preview.canView && preview.limit && (
              <div
                data-testid="sneak-peek-banner"
                onClick={() => navigate("/pricing")}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, padding: "0.7rem 1rem",
                  background: preview.remaining === 0 ? "rgba(245,158,11,0.06)" : "rgba(200,255,0,0.05)",
                  border: `1px solid ${preview.remaining === 0 ? "rgba(245,158,11,0.45)" : "rgba(200,255,0,0.4)"}`,
                  color: preview.remaining === 0 ? "#FCD34D" : "#C8FF00",
                  cursor: "pointer",
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.74rem",
                  boxShadow: preview.remaining === 0
                    ? "0 0 18px rgba(245,158,11,0.25)"
                    : "0 0 14px rgba(200,255,0,0.18)",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Gift size={14} />
                  {preview.alreadyViewed
                    ? `Anteprima Premium · partita già sbloccata (${preview.used}/${preview.limit} usate)`
                    : preview.remaining === 0
                      ? `🔥 ULTIMA anteprima Premium · ${preview.used}/${preview.limit} usate · paywall attivato sulle prossime`
                      : `Anteprima Premium · ${preview.used}/${preview.limit} partite gratuite · ${preview.remaining} rimanenti`}
                </span>
                <span style={{
                  padding: "0.25rem 0.6rem",
                  border: "1px solid currentColor", borderRadius: 2,
                  fontSize: "0.62rem", letterSpacing: "0.16em", textTransform: "uppercase",
                  fontWeight: 700,
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}>
                  <SparklesIcon size={10} /> Sblocca tutto →
                </span>
              </div>
            )}
            {/* Paywall: scenari distinti per Free
                a) Ha ancora credito sneak peek → CTA "Sblocca questa partita gratis" (manuale)
                b) Credito esaurito → Paywall Premium */}
            {!isPremium && !preview.canView && !preview.loading && preview.canUnlock && (
              <div
                data-testid="sneak-peek-unlock-cta"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, padding: "0.9rem 1.1rem",
                  background: "rgba(200,255,0,0.08)",
                  border: "1px solid rgba(200,255,0,0.55)",
                  color: "#C8FF00",
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.78rem",
                  boxShadow: "0 0 22px rgba(200,255,0,0.30)",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: "1 1 60%" }}>
                  <Gift size={14} />
                  Hai ancora 1 anteprima Premium gratuita. Sblocca questa partita per vedere TUTTI i dati IA?
                </span>
                <button
                  data-testid="sneak-peek-unlock-btn"
                  disabled={preview.unlocking}
                  onClick={async () => { await preview.unlock(); }}
                  style={{
                    padding: "0.5rem 1rem",
                    background: "#C8FF00", color: "#020202",
                    border: "none", cursor: preview.unlocking ? "wait" : "pointer",
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: "0.78rem", letterSpacing: "0.16em", textTransform: "uppercase",
                    fontWeight: 800,
                    opacity: preview.unlocking ? 0.7 : 1,
                  }}
                >
                  {preview.unlocking ? "Sblocco…" : "🔓 Sblocca questa partita"}
                </button>
                {preview.unlockError && (
                  <div style={{ flexBasis: "100%", color: "#FCA5A5", fontSize: "0.7rem" }}>
                    {preview.unlockError}
                  </div>
                )}
              </div>
            )}
            {!isPremium && !preview.canView && !preview.loading && !preview.canUnlock && (
              <div
                data-testid="sneak-peek-exhausted-banner"
                onClick={() => navigate("/pricing")}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, padding: "0.9rem 1.1rem",
                  background: "rgba(200,255,0,0.06)",
                  border: "1px solid rgba(200,255,0,0.55)",
                  color: "#C8FF00",
                  cursor: "pointer",
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.78rem",
                  boxShadow: "0 0 20px rgba(200,255,0,0.30)",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Gift size={14} />
                  Hai già usato la tua anteprima Premium gratuita — sblocca Premium per accedere a tutte le partite
                </span>
                <span style={{
                  padding: "0.3rem 0.7rem",
                  background: "#C8FF00", color: "#020202",
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase",
                  fontWeight: 800,
                }}>
                  Sblocca Premium →
                </span>
              </div>
            )}

            {/* Play Profile selector + Tachimetro (con master switch) — Premium */}
            <PremiumGate
              feature="Profilo di Gioco"
              reason="Tachimetro personalizzato per stile"
              testid="premium-gate-play-profile"
              forceUnlock={forceUnlock}
            >
              <PlayProfilePanel
                activeProfile={playProfile}
                onChange={onProfileChange}
                enabled={profileEnabled}
                onToggleEnabled={onProfileToggle}
                predictions={predictions}
              />
            </PremiumGate>

            {/* Match DNA — subito sotto i Profili di Gioco,
                posizione fissa indipendente dal profilo selezionato */}
            <MatchDNAPanel
              fixtureId={data.fixture?.id || fixtureId}
              homeTeamName={home_team?.name}
              awayTeamName={away_team?.name}
            />

            {/* Pannelli riordinati in base al Profilo di Gioco (o ordine classico) */}
            {renderPronosticiPanels({
              profile: playProfile,
              profileEnabled,
              data,
              predictions,
              home_team,
              away_team,
              handleAddBet,
              selectedBets,
              isPremium,
              forceUnlock,
            })}

            {/* Floating save bar */}
            {selectedBets.length > 0 && (
              <div className="sticky bottom-4 z-30" data-testid="save-bets-bar">
                <div className="flex items-center justify-between gap-3 px-4 py-3 shadow-lg"
                  style={{ background: "rgba(59,130,246,0.95)", backdropFilter: "blur(8px)" }}>
                  <div className="flex items-center gap-2">
                    <ShoppingCart size={16} color="#fff" />
                    <span className="font-heading font-bold text-sm text-white uppercase tracking-wider">
                      {selectedBets.length} pronostic{selectedBets.length === 1 ? "o" : "i"} selezionat{selectedBets.length === 1 ? "o" : "i"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {showAddedMsg && (
                      <span className="font-data text-xs text-white flex items-center gap-1">
                        <CheckCircle2 size={12} /> Salvato!
                      </span>
                    )}
                    <button data-testid="save-bets-btn" onClick={saveToMyBets}
                      className="px-4 py-1.5 font-heading font-bold text-xs uppercase tracking-wider transition-all hover:opacity-90"
                      style={{ background: "#fff", color: "#3B82F6" }}>
                      Salva nei Miei Pronostici
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* AI ANALYSIS — trigger manuale */}
            {AI_ENABLED && (
              aiRequested
                ? <AIAnalysisPanel aiAnalysis={aiAnalysis} loading={aiLoading} />
                : (
                  <div className="flex flex-col items-center justify-center gap-4 py-10 px-6"
                    style={{ border: `1px dashed rgba(6,182,212,0.25)`, background: "rgba(6,182,212,0.03)" }}>
                    <div className="flex flex-col items-center gap-1 text-center">
                      <span className="font-heading font-black text-xs uppercase tracking-[0.2em]" style={{ color: C.cyan }}>
                        Valutazione AI
                      </span>
                      <p className="font-data text-xs" style={{ color: C.muted }}>
                        Analisi testuale avanzata tramite Claude Sonnet — forma, H2H, metriche avanzate.
                      </p>
                    </div>
                    <button
                      data-testid="start-ai-analysis-btn"
                      onClick={fetchAI}
                      className="flex items-center gap-2 px-5 py-2.5 font-heading font-black text-xs uppercase tracking-widest transition-all hover:opacity-90 active:scale-95"
                      style={{ background: C.cyan, color: "#020202" }}>
                      <Zap size={13} />
                      Avvia Analisi AI
                    </button>
                  </div>
                )
            )}

            {/* ── DETTAGLI TECNICI (in fondo per non distrarre dai pronostici) ── */}
            <div className="pt-2">
              <div
                className="flex items-center gap-2 mb-3 px-1"
                style={{ borderTop: `1px solid var(--bc-border, rgba(255,255,255,0.07))`, paddingTop: 16 }}
              >
                <span
                  className="font-heading font-black text-[10px] uppercase tracking-[0.25em]"
                  style={{ color: C.muted }}
                >
                  Dettagli Tecnici · Modelli & Algoritmi
                </span>
              </div>
              <HoverPanel color="rgba(245,158,11,0.18)" borderGlow="rgba(245,158,11,0.32)">
                <AlgorithmPanel
                  predictions={predictions}
                  homeTeam={home_team.name}
                  awayTeam={away_team.name}
                />
              </HoverPanel>
            </div>
          </div>
        )}

        {activeTab === "statistiche" && (
          <div className="space-y-4">
            {/* 1. STATISTICHE PARTITA — real data, only for finished/live */}
            {(isFinished || isLive) && (real_stats?.home || real_stats?.away) && (
              <HoverPanel color="rgba(34,197,94,0.18)" borderGlow="rgba(34,197,94,0.32)">
                <StatsComparisonPanel
                  home={real_stats.home}
                  away={real_stats.away}
                  homeName={home_team.name}
                  awayName={away_team.name}
                  variant="real"
                />
              </HoverPanel>
            )}

            {/* 2. STATISTICHE PREVISTE — always shown (pre-match model projection) */}
            {predicted_stats && (
              <HoverPanel color="rgba(59,130,246,0.18)" borderGlow="rgba(59,130,246,0.32)">
                <StatsComparisonPanel
                  home={predicted_stats.home}
                  away={predicted_stats.away}
                  homeName={home_team.name}
                  awayName={away_team.name}
                  variant="predicted"
                />
              </HoverPanel>
            )}

            {/* 3. MEDIE STAGIONALI — season baseline, same 8 metrics */}
            {season_avg_stats && (
              <HoverPanel color="rgba(167,139,250,0.18)" borderGlow="rgba(167,139,250,0.32)">
                <StatsComparisonPanel
                  home={season_avg_stats.home}
                  away={season_avg_stats.away}
                  homeName={home_team.name}
                  awayName={away_team.name}
                  variant="season"
                />
              </HoverPanel>
            )}

            {/* 4. Form Guide - both teams */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <HoverPanel color="rgba(34,197,94,0.18)" borderGlow="rgba(34,197,94,0.32)">
                <FormGuide team={home_team} form={home_form} stats={home_stats} side="home" />
              </HoverPanel>
              <HoverPanel color="rgba(239,68,68,0.18)" borderGlow="rgba(239,68,68,0.32)">
                <FormGuide team={away_team} form={away_form} stats={away_stats} side="away" />
              </HoverPanel>
            </div>

            {/* 5. Referee & Injuries row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <HoverPanel color="rgba(245,158,11,0.18)" borderGlow="rgba(245,158,11,0.32)">
                <RefereePanel referee={fixture.referee} venue={fixture.venue} stats={data.referee_stats} />
              </HoverPanel>
              <HoverPanel color="rgba(239,68,68,0.18)" borderGlow="rgba(239,68,68,0.32)">
                <InjuriesPanel injuries={injuries || []} homeTeamId={home_team.id} awayTeamId={away_team.id} />
              </HoverPanel>
            </div>

            {/* 6. H2H Panel */}
            <H2HPanel
              matches={h2h}
              homeTeam={home_team}
              awayTeam={away_team}
            />

            {/* 7. Standings */}
            {standings.length > 0 && (
              <StandingsTable
                standings={standings}
                homeTeamId={home_team.id}
                awayTeamId={away_team.id}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
